/**
 * Onglet "Agenda" du back-office B2B.
 *
 * 3 vues : Mois (grille 7x6), Semaine (7 colonnes), Liste (chronologique).
 *
 * Sources des événements :
 *   - Leads planifiés (champs scheduled_start/end saisis par l'admin)
 *   - Documents (devis/facture/contrat) avec intervention_start/end
 *   - Événements manuels (vacances, indispos)
 *
 * Couleurs par type de prestation : récupérées dynamiquement depuis /agenda/meta.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameDay, isSameMonth, eachDayOfInterval, parseISO,
  startOfDay, endOfDay, isWithinInterval, addWeeks, isToday,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, CalendarDays, Plus, Trash2, Clock,
  Calendar as CalendarIcon, List, LayoutGrid, MapPin, User, FileText,
} from 'lucide-react';
import api from '../../../api';
import { toast } from 'sonner';

const VIEWS = ['month', 'week', 'list'];
const VIEW_LABELS = { month: 'Mois', week: 'Semaine', list: 'Liste' };

const fmtDateInput = (d) => format(d, "yyyy-MM-dd'T'HH:mm");
const parseInput = (s) => {
  // s = "2026-06-15T09:00" (datetime-local) → ISO UTC
  if (!s) return null;
  const d = new Date(s);
  return d.toISOString();
};

const EVENT_TYPES = [
  { value: 'vacation', label: 'Vacances' },
  { value: 'blocker', label: 'Indisponibilité' },
  { value: 'personal', label: 'Personnel' },
  { value: 'training', label: 'Formation' },
  { value: 'other', label: 'Autre' },
];

const EMPTY_EVENT = {
  title: '',
  start: '',
  end: '',
  all_day: false,
  event_type: 'blocker',
  prestation_type: '',
  notes: '',
};

// ============================================================================
// COMPOSANTS DE VUE
// ============================================================================

const EventChip = ({ ev, onClick, compact = false }) => {
  const color = ev.color || '#94a3b8';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      className="block w-full text-left rounded px-1.5 py-0.5 text-[10px] font-bold leading-tight truncate hover:brightness-110 transition-all"
      style={{ backgroundColor: `${color}22`, color, borderLeft: `3px solid ${color}` }}
      data-testid={`agenda-event-${ev.id}`}
      title={ev.title}
    >
      {!compact && ev.start && !ev.all_day && (
        <span className="opacity-75 mr-1">{format(parseISO(ev.start), 'HH:mm')}</span>
      )}
      {ev.title}
    </button>
  );
};

const MonthView = ({ currentDate, events, onEventClick, onDayClick }) => {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }); // lundi
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const evStart = ev.start ? parseISO(ev.start) : null;
      const evEnd = ev.end ? parseISO(ev.end) : evStart;
      if (!evStart) continue;
      for (const d of days) {
        if (isWithinInterval(d, { start: startOfDay(evStart), end: endOfDay(evEnd) })) {
          const k = format(d, 'yyyy-MM-dd');
          if (!map.has(k)) map.set(k, []);
          map.get(k).push(ev);
        }
      }
    }
    return map;
  }, [events, days]);

  const weekDayLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid="agenda-month-view">
      <div className="grid grid-cols-7 bg-muted/50 border-b border-border">
        {weekDayLabels.map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-fr">
        {days.map((d) => {
          const k = format(d, 'yyyy-MM-dd');
          const dayEvents = eventsByDay.get(k) || [];
          const isCurrentMonth = isSameMonth(d, currentDate);
          const today = isToday(d);
          return (
            <button
              key={k}
              type="button"
              onClick={() => onDayClick(d)}
              className={`min-h-[100px] p-1.5 border-r border-b border-border last:border-r-0 text-left transition-colors hover:bg-muted/30
                ${isCurrentMonth ? '' : 'bg-muted/20 opacity-50'}
                ${today ? 'bg-[hsl(var(--primary))]/5' : ''}`}
              data-testid={`agenda-day-${k}`}
            >
              <div className={`text-xs font-black mb-1 ${today ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'}`}>
                {format(d, 'd')}
                {today && <span className="ml-1 text-[9px] uppercase tracking-wider">aujourd'hui</span>}
              </div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map((ev) => (
                  <EventChip key={ev.id} ev={ev} onClick={onEventClick} compact />
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[9px] text-muted-foreground font-bold">+{dayEvents.length - 3} autre{dayEvents.length - 3 > 1 ? 's' : ''}</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const WeekView = ({ currentDate, events, onEventClick }) => {
  const days = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end: endOfWeek(currentDate, { weekStartsOn: 1 }) });
  }, [currentDate]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const evStart = ev.start ? parseISO(ev.start) : null;
      const evEnd = ev.end ? parseISO(ev.end) : evStart;
      if (!evStart) continue;
      for (const d of days) {
        if (isWithinInterval(d, { start: startOfDay(evStart), end: endOfDay(evEnd) })) {
          const k = format(d, 'yyyy-MM-dd');
          if (!map.has(k)) map.set(k, []);
          map.get(k).push(ev);
        }
      }
    }
    return map;
  }, [events, days]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="agenda-week-view">
      {days.map((d) => {
        const k = format(d, 'yyyy-MM-dd');
        const dayEvents = (eventsByDay.get(k) || []).sort((a, b) => (a.start || '').localeCompare(b.start || ''));
        const today = isToday(d);
        return (
          <Card key={k} className={today ? 'border-[hsl(var(--primary))]' : ''}>
            <CardContent className="p-2 min-h-[200px]">
              <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${today ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'}`}>
                {format(d, 'EEE d MMM', { locale: fr })}
              </div>
              <div className="space-y-1">
                {dayEvents.length === 0 ? (
                  <div className="text-[10px] text-muted-foreground italic opacity-60">—</div>
                ) : (
                  dayEvents.map((ev) => (
                    <EventChip key={ev.id} ev={ev} onClick={onEventClick} />
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const ListView = ({ events, onEventClick }) => {
  const grouped = useMemo(() => {
    const map = new Map();
    const sorted = [...events].sort((a, b) => (a.start || '').localeCompare(b.start || ''));
    for (const ev of sorted) {
      const evStart = ev.start ? parseISO(ev.start) : null;
      if (!evStart) continue;
      const k = format(evStart, 'yyyy-MM-dd');
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    }
    return Array.from(map.entries());
  }, [events]);

  if (grouped.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-sm text-muted-foreground">
          Aucun événement sur cette période.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="agenda-list-view">
      {grouped.map(([dateKey, dayEvents]) => {
        const d = parseISO(dateKey + 'T00:00:00');
        return (
          <Card key={dateKey} className={isToday(d) ? 'border-[hsl(var(--primary))]' : ''}>
            <CardContent className="p-4">
              <div className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-2">
                {format(d, 'EEEE d MMMM yyyy', { locale: fr })}
                {isToday(d) && <span className="ml-2 text-[hsl(var(--primary))]">· Aujourd'hui</span>}
              </div>
              <div className="space-y-2">
                {dayEvents.map((ev) => (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => onEventClick(ev)}
                    className="block w-full text-left p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                    style={{ borderLeftWidth: '4px', borderLeftColor: ev.color }}
                    data-testid={`agenda-list-event-${ev.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm flex items-center gap-2">
                          <span style={{ color: ev.color }}>●</span>
                          {ev.title}
                        </div>
                        {ev.subtitle && <div className="text-xs text-muted-foreground mt-0.5">{ev.subtitle}</div>}
                        {ev.notes && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{ev.notes}</div>}
                      </div>
                      <div className="text-xs text-muted-foreground flex-shrink-0 text-right">
                        {ev.all_day ? (
                          <div className="font-bold">Journée entière</div>
                        ) : (
                          <>
                            <div className="font-bold">{ev.start ? format(parseISO(ev.start), 'HH:mm') : '—'}</div>
                            {ev.end && <div className="opacity-70">→ {format(parseISO(ev.end), 'HH:mm')}</div>}
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export const B2BAgendaTab = () => {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ prestation_types: [], event_types: [] });
  const [activeFilters, setActiveFilters] = useState(new Set()); // prestation_type values

  // Détail
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Création événement manuel
  const [createOpen, setCreateOpen] = useState(false);
  const [evForm, setEvForm] = useState(EMPTY_EVENT);
  const [savingEvent, setSavingEvent] = useState(false);

  const range = useMemo(() => {
    if (view === 'month') {
      const s = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      const e = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
      return { start: s.toISOString(), end: e.toISOString() };
    }
    if (view === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }).toISOString(),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }).toISOString(),
      };
    }
    // list = 3 mois centrés sur currentDate
    return {
      start: addMonths(startOfMonth(currentDate), -1).toISOString(),
      end: addMonths(endOfMonth(currentDate), 1).toISOString(),
    };
  }, [view, currentDate]);

  const loadMeta = async () => {
    try {
      const m = await api.b2bAgendaMeta();
      setMeta(m);
    } catch (e) { /* silent */ }
  };

  const loadEvents = async () => {
    try {
      const r = await api.b2bAgendaList(range);
      setEvents(r.events || []);
    } catch (e) {
      toast.error('Erreur chargement agenda');
    }
  };

  useEffect(() => { loadMeta(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadEvents(); }, [range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredEvents = useMemo(() => {
    if (activeFilters.size === 0) return events;
    return events.filter((ev) => activeFilters.has(ev.prestation_type || 'other'));
  }, [events, activeFilters]);

  const toggleFilter = (pt) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(pt)) next.delete(pt); else next.add(pt);
      return next;
    });
  };

  const navPrev = () => {
    if (view === 'month' || view === 'list') setCurrentDate(addMonths(currentDate, -1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, -1));
  };
  const navNext = () => {
    if (view === 'month' || view === 'list') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
  };
  const navToday = () => setCurrentDate(new Date());

  const openCreate = (defaultDate) => {
    const base = defaultDate || new Date();
    const start = new Date(base);
    start.setHours(9, 0, 0, 0);
    const end = new Date(base);
    end.setHours(17, 0, 0, 0);
    setEvForm({
      ...EMPTY_EVENT,
      start: fmtDateInput(start),
      end: fmtDateInput(end),
    });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!evForm.title || evForm.title.trim().length < 2) {
      toast.error('Le titre est obligatoire');
      return;
    }
    if (!evForm.start || !evForm.end) {
      toast.error('Dates de début et fin obligatoires');
      return;
    }
    setSavingEvent(true);
    try {
      const payload = {
        title: evForm.title.trim(),
        start: parseInput(evForm.start),
        end: parseInput(evForm.end),
        all_day: !!evForm.all_day,
        event_type: evForm.event_type,
        prestation_type: evForm.prestation_type || null,
        notes: evForm.notes || null,
      };
      await api.b2bCreateEvent(payload);
      toast.success('Événement créé');
      setCreateOpen(false);
      await loadEvents();
    } catch (e) {
      toast.error(e.message || 'Erreur création');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (ev) => {
    if (ev.source !== 'manual') {
      toast.error('Seuls les événements manuels peuvent être supprimés ici');
      return;
    }
    if (!window.confirm(`Supprimer l'événement "${ev.title}" ?`)) return;
    try {
      await api.b2bDeleteEvent(ev.event_id);
      toast.success('Événement supprimé');
      setSelectedEvent(null);
      await loadEvents();
    } catch (e) {
      toast.error(e.message || 'Erreur suppression');
    }
  };

  const periodLabel = useMemo(() => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy', { locale: fr });
    if (view === 'week') {
      const s = startOfWeek(currentDate, { weekStartsOn: 1 });
      const e = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `Semaine du ${format(s, 'd MMM', { locale: fr })} au ${format(e, 'd MMM yyyy', { locale: fr })}`;
    }
    return `${format(addMonths(currentDate, -1), 'MMM', { locale: fr })} → ${format(addMonths(currentDate, 1), 'MMM yyyy', { locale: fr })}`;
  }, [view, currentDate]);

  return (
    <div className="space-y-4" data-testid="agenda-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={navPrev} data-testid="agenda-prev"><ChevronLeft size={16} /></Button>
          <Button size="sm" variant="outline" onClick={navToday} data-testid="agenda-today">Aujourd'hui</Button>
          <Button size="sm" variant="outline" onClick={navNext} data-testid="agenda-next"><ChevronRight size={16} /></Button>
          <h3 className="text-sm font-black ml-3 capitalize">{periodLabel}</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            {VIEWS.map((v) => (
              <Button
                key={v}
                size="sm"
                variant={view === v ? 'default' : 'ghost'}
                onClick={() => setView(v)}
                className="rounded-none h-8 text-xs"
                data-testid={`agenda-view-${v}`}
              >
                {v === 'month' && <LayoutGrid size={14} className="mr-1" />}
                {v === 'week' && <CalendarIcon size={14} className="mr-1" />}
                {v === 'list' && <List size={14} className="mr-1" />}
                {VIEW_LABELS[v]}
              </Button>
            ))}
          </div>
          <Button size="sm" onClick={() => openCreate()} data-testid="agenda-create-event">
            <Plus size={14} className="mr-1" /> Nouvel événement
          </Button>
        </div>
      </div>

      {/* Légende couleurs */}
      {meta.prestation_types.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mr-1">Filtrer par prestation :</div>
              {meta.prestation_types.map((pt) => {
                const active = activeFilters.has(pt.value);
                return (
                  <button
                    key={pt.value}
                    type="button"
                    onClick={() => toggleFilter(pt.value)}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide transition-all ${active ? 'opacity-100 ring-2' : 'opacity-50 hover:opacity-80'}`}
                    style={{ backgroundColor: `${pt.color}22`, color: pt.color, '--tw-ring-color': pt.color }}
                    data-testid={`agenda-filter-${pt.value}`}
                  >
                    <span style={{ backgroundColor: pt.color }} className="w-2 h-2 rounded-full" />
                    {pt.label}
                  </button>
                );
              })}
              {activeFilters.size > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setActiveFilters(new Set())} className="h-6 text-xs">
                  Réinitialiser
                </Button>
              )}
              <div className="ml-auto text-xs text-muted-foreground">
                {filteredEvents.length} / {events.length} événement{events.length > 1 ? 's' : ''}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vue active */}
      {view === 'month' && (
        <MonthView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={setSelectedEvent}
          onDayClick={openCreate}
        />
      )}
      {view === 'week' && (
        <WeekView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={setSelectedEvent}
        />
      )}
      {view === 'list' && (
        <ListView events={filteredEvents} onEventClick={setSelectedEvent} />
      )}

      {/* DIALOG DÉTAIL ÉVÉNEMENT */}
      <Dialog open={!!selectedEvent} onOpenChange={(o) => { if (!o) setSelectedEvent(null); }}>
        <DialogContent className="max-w-lg" data-testid="agenda-event-dialog">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedEvent.color }} />
                  {selectedEvent.title}
                </DialogTitle>
                <DialogDescription>
                  {selectedEvent.source === 'lead' && 'Demande planifiée (lead /prestations-pro)'}
                  {selectedEvent.source === 'document' && `Prestation liée à ${selectedEvent.document_number}`}
                  {selectedEvent.source === 'manual' && 'Événement manuel'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-muted-foreground" />
                  {selectedEvent.all_day ? 'Journée entière' : (
                    <>
                      {selectedEvent.start ? format(parseISO(selectedEvent.start), 'EEEE d MMMM, HH:mm', { locale: fr }) : '—'}
                      {selectedEvent.end && <> → {format(parseISO(selectedEvent.end), 'HH:mm')}</>}
                    </>
                  )}
                </div>
                {selectedEvent.prestation_type && (
                  <div>
                    <strong>Prestation :</strong> {meta.prestation_types.find(p => p.value === selectedEvent.prestation_type)?.label || selectedEvent.prestation_type}
                  </div>
                )}
                {selectedEvent.subtitle && (
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-muted-foreground" />
                    {selectedEvent.subtitle}
                  </div>
                )}
                {selectedEvent.amount && (
                  <div><strong>Montant :</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(selectedEvent.amount)}</div>
                )}
                {selectedEvent.notes && (
                  <div className="p-3 rounded-lg bg-muted/40 border border-border whitespace-pre-wrap text-xs">
                    {selectedEvent.notes}
                  </div>
                )}
                {selectedEvent.source !== 'manual' && (
                  <div className="text-xs text-muted-foreground italic">
                    Pour modifier les dates, utilisez l'onglet correspondant ({selectedEvent.source === 'lead' ? 'Leads' : 'Devis/Factures/Contrats'}).
                  </div>
                )}
              </div>
              <DialogFooter>
                {selectedEvent.source === 'manual' && (
                  <Button variant="outline" className="text-rose-600" onClick={() => handleDeleteEvent(selectedEvent)} data-testid="agenda-event-delete">
                    <Trash2 size={14} className="mr-1.5" /> Supprimer
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelectedEvent(null)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* DIALOG CRÉATION ÉVÉNEMENT MANUEL */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="agenda-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouvel événement</DialogTitle>
            <DialogDescription>Vacances, indisponibilité ou formation perso</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titre *</Label>
              <Input
                value={evForm.title}
                onChange={(e) => setEvForm({ ...evForm, title: e.target.value })}
                placeholder="Vacances été, Formation continue, Indispo médecin…"
                data-testid="agenda-form-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={evForm.event_type} onValueChange={(v) => setEvForm({ ...evForm, event_type: v })}>
                  <SelectTrigger data-testid="agenda-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prestation liée (optionnel)</Label>
                <Select value={evForm.prestation_type || 'none'} onValueChange={(v) => setEvForm({ ...evForm, prestation_type: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucune —</SelectItem>
                    {meta.prestation_types.map((pt) => (
                      <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Début *</Label>
                <Input
                  type="datetime-local"
                  value={evForm.start}
                  onChange={(e) => setEvForm({ ...evForm, start: e.target.value })}
                  data-testid="agenda-form-start"
                />
              </div>
              <div>
                <Label>Fin *</Label>
                <Input
                  type="datetime-local"
                  value={evForm.end}
                  onChange={(e) => setEvForm({ ...evForm, end: e.target.value })}
                  data-testid="agenda-form-end"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="all-day"
                checked={evForm.all_day}
                onChange={(e) => setEvForm({ ...evForm, all_day: e.target.checked })}
              />
              <Label htmlFor="all-day" className="cursor-pointer">Toute la journée</Label>
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                rows={2}
                value={evForm.notes}
                onChange={(e) => setEvForm({ ...evForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={savingEvent}>Annuler</Button>
            <Button onClick={handleCreate} disabled={savingEvent} data-testid="agenda-form-save">
              {savingEvent ? 'Création…' : 'Créer l\'événement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
